/**
 * The hover card on a KPI value (DOP-609): what this number's look means, where
 * it came from, and what a click will do.
 *
 * The grid's cell states are deliberately quiet, so a reader has to be TOLD
 * what a dotted underline means — and the moment they are looking at one is
 * the moment to tell them. The native `title` it replaces said the same thing
 * after a long delay in the OS font, which nobody on the Summit call noticed.
 *
 * Positioned `fixed` from the cell's rect so the grid's own scroll container
 * cannot clip it on the last rows or the right-most column. Below the value by
 * default; measured once drawn, and flipped above it when the space below is
 * too short (the last visible rows), then held inside the viewport.
 */

import React, { useLayoutEffect, useRef, useState } from "react";

import type { KpiCellState } from "../../types/kpi";
import { KPI_CELL_STATE_TITLE } from "./kpiCellStates";

const WIDTH = 300;
const EDGE = 16;

export interface KpiCellTooltipProps {
	state: KpiCellState;
	/** The formatted value, as the cell shows it. */
	valueText: string;
	/** The file the value was read from, when there is exactly one. */
	sourceFile: string | null;
	/** What a click does: open that file, or show the breakdown first. */
	clickOpensFile: boolean;
	/** The hovered value's box, in viewport coordinates. */
	anchor: DOMRect;
	id: string;
}

function body(
	state: KpiCellState,
	valueText: string,
	sourceFile: string | null,
): React.ReactNode {
	const strong = (text: string) => <b className="font-semibold text-white">{text}</b>;
	switch (state) {
		case "verified":
			return sourceFile ? (
				<>Doppler matched {strong(valueText)} to the cell it came from in {strong(sourceFile)}.</>
			) : (
				<>Doppler matched {strong(valueText)} to the numbers it was built from.</>
			);
		case "unverified":
			return sourceFile ? (
				<>
					Doppler read {strong(valueText)} from {strong(sourceFile)} but couldn't
					match it to a single cell. Worth a quick look.
				</>
			) : (
				<>
					Doppler found {strong(valueText)} but couldn't check it against a source
					yet. Worth a quick look.
				</>
			);
		case "overridden":
			return <>Someone on your team replaced the extracted number. The original stays on record.</>;
		default:
			return <>Nothing was found for this period.</>;
	}
}

function marker(state: KpiCellState): React.ReactNode {
	if (state === "unverified") {
		return <span className="h-[3px] w-[18px] border-b-2 border-dotted border-[#d97706]" />;
	}
	if (state === "overridden") {
		return <span className="h-2 w-2 rounded-full bg-[#99CFCD]" />;
	}
	if (state === "verified") {
		return <span className="h-2 w-2 rounded-full bg-[#34d399]" />;
	}
	return <span className="text-[#b8bcc2]">—</span>;
}

const KpiCellTooltip: React.FC<KpiCellTooltipProps> = ({
	state,
	valueText,
	sourceFile,
	clickOpensFile,
	anchor,
	id,
}) => {
	const viewportWidth =
		typeof window === "undefined" ? 1280 : window.innerWidth;
	const center = anchor.left + anchor.width / 2;
	const left = Math.max(
		EDGE,
		Math.min(center - WIDTH / 2, viewportWidth - WIDTH - EDGE),
	);
	// The arrow stays over the value even when the card is pushed off-centre
	// by the viewport edge.
	const arrowLeft = Math.max(12, Math.min(center - left - 6, WIDTH - 24));

	const cardRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState<number | null>(null);
	useLayoutEffect(() => {
		setHeight(cardRef.current?.offsetHeight ?? null);
	}, [state, valueText, sourceFile]);
	const viewportHeight =
		typeof window === "undefined" ? 800 : window.innerHeight;
	const below = anchor.bottom + 8;
	const above = height === null ? below : anchor.top - 8 - height;
	const flip =
		height !== null && below + height > viewportHeight - EDGE && above >= EDGE;
	const top = flip
		? above
		: height === null
			? below
			: Math.max(EDGE, Math.min(below, viewportHeight - height - EDGE));
	const action =
		state === "empty"
			? "Click to see why"
			: clickOpensFile
				? "Click to open the file at this number"
				: "Click to see where it came from";

	return (
		<div
			ref={cardRef}
			id={id}
			role="tooltip"
			className="pointer-events-none fixed z-50 flex flex-col gap-1.5 rounded-[10px] bg-[#111827] px-3.5 py-3 text-white shadow-[0_16px_36px_rgba(17,24,39,0.3)]"
			style={{ left, top, width: WIDTH }}
		>
			<div className="flex items-center gap-2 text-sm font-semibold">
				{marker(state)}
				{KPI_CELL_STATE_TITLE[state]}
			</div>
			<div className="text-[12.5px] leading-normal text-[#d1d5db]">
				{body(state, valueText, sourceFile)}
			</div>
			<div className="text-xs font-semibold text-[#99CFCD]">{action} →</div>
			<div
				aria-hidden="true"
				className={`absolute h-3 w-3 rotate-45 bg-[#111827] ${
					flip ? "-bottom-1.5" : "-top-1.5"
				}`}
				style={{ left: arrowLeft }}
			/>
		</div>
	);
};

export default KpiCellTooltip;
